import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/api';
import { format, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Sidebar } from '@/components/Sidebar';
import { CalendarView } from '@/components/CalendarView';
import { subtractAppointmentsFromGoogleBusy } from '@/lib/calendarEvents';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, XCircle, CalendarDays, Search, LayoutGrid, List, UserX, UserMinus, Repeat, UserPlus, FileText, DoorOpen, ChevronLeft, ChevronRight, X, Pencil, Trash2, BellRing } from 'lucide-react';
import { useIndustry } from '@/components/IndustryContext';
import { formatMoney } from '@/lib/currency';
import { timezoneLabel } from '@/lib/timezones';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending_admin: { label: 'Ожидает админа', className: 'bg-orange-900/30 text-orange-400 border border-orange-900/50' },
  pending_doctor: { label: 'Ожидает врача', className: 'bg-sky-900/30 text-sky-400 border border-sky-900/50' },
  pending: { label: 'Ожидает', className: 'bg-amber-900/30 text-amber-400 border border-amber-900/50' },
  confirmed: { label: 'Подтверждена', className: 'bg-green-900/30 text-green-400 border border-green-900/50' },
  cancelled: { label: 'Отменена', className: 'bg-red-900/30 text-red-400 border border-red-900/50' },
  no_show_patient: { label: 'Неявка клиента', className: 'bg-violet-900/30 text-violet-400 border border-violet-900/50' },
  no_show_doctor: { label: 'Неявка психолога', className: 'bg-blue-900/30 text-blue-400 border border-blue-900/50' },
  waiting: { label: 'В приёмной', className: 'bg-amber-900/30 text-amber-300 border border-amber-800/50' },
  in_progress: { label: 'На приёме', className: 'bg-violet-900/30 text-violet-300 border border-violet-800/50' },
  completed: { label: 'Завершён', className: 'bg-emerald-900/30 text-emerald-400 border border-emerald-900/50' },
  paid: { label: 'Оплачен', className: 'bg-stone-800/50 text-stone-400 border border-stone-700/50' },
};

export default function AppointmentsPage() {
  const [appointments, setAppointments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [viewMode, setViewMode] = useState('calendar');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [slotDialogData, setSlotDialogData] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [newDate, setNewDate] = useState(null);
  const [newTime, setNewTime] = useState('');
  const [loading, setLoading] = useState(true);
  const [recurOpen, setRecurOpen] = useState(false);
  const [recurType, setRecurType] = useState('weekly');
  const [recurCount, setRecurCount] = useState(4);
  const [sessionNotes, setSessionNotes] = useState('');
  const [linkPatientOpen, setLinkPatientOpen] = useState(false);
  const [linkPatientSearch, setLinkPatientSearch] = useState('');
  const [editApptOpen, setEditApptOpen] = useState(false);
  const [editAppt, setEditAppt] = useState({ format_id: '', notes: '' });
  const [editGroupPatients, setEditGroupPatients] = useState([]);
  const [editGroupSearch, setEditGroupSearch] = useState('');
  const [newApptSlots, setNewApptSlots] = useState([]);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [userRole, setUserRole] = useState('doctor');
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState(1); // 1=client, 2=format, 3=datetime
  const [newAppt, setNewAppt] = useState({ patient_name: '', patient_phone: '', patient_email: '', date: '', time: '', format_id: '', notes: '', room_id: '' });
  const [creating, setCreating] = useState(false);
  const [patients, setPatients] = useState([]);
  const [groupMode, setGroupMode] = useState(false);
  const [groupPatients, setGroupPatients] = useState([]);
  const [groupPatientSearch, setGroupPatientSearch] = useState('');
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [filterDoctorId, setFilterDoctorId] = useState('');
  const [isClinicRole, setIsClinicRole] = useState(false);
  const [doctorSchedule, setDoctorSchedule] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [googleBusy, setGoogleBusy] = useState([]);
  const [googleBusyWarning, setGoogleBusyWarning] = useState('');
  const [calendarRange, setCalendarRange] = useState(null);
  const [telegramStatus, setTelegramStatus] = useState(null);
  const notesTimerRef = useRef(null);
  const notesApptRef = useRef(null);
  const navigate = useNavigate();

  // Fetch selected doctor's schedule (working_hours, formats, overrides)
  const fetchDoctorSchedule = useCallback(async (docId) => {
    if (!docId) { setDoctorSchedule(null); return; }
    try {
      const { data } = await api.get(`/team/doctors/${docId}/schedule`);
      setDoctorSchedule(data);
    } catch { setDoctorSchedule(null); }
  }, []);

  const fetchAppointments = useCallback(async () => {
    try {
      const params = filterDoctorId ? `?doctor_id=${filterDoctorId}` : '';
      const [apptRes, settingsRes, overridesRes] = await Promise.all([
        api.get(`/appointments${params}`),
        api.get(`/settings`),
        api.get(`/schedule-overrides`),
      ]);
      setAppointments(Array.isArray(apptRes.data) ? apptRes.data : []);
      setSettings(settingsRes.data);
      setOverrides(overridesRes.data);
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    }
    setLoading(false);
  }, [navigate, filterDoctorId]);

  useEffect(() => {
    if (!document.cookie.includes('pp_csrf')) { navigate('/login'); return; }
    fetchAppointments();
    api.get('/telegram/status', { params: { issue_link: false } })
      .then(({ data }) => setTelegramStatus(data))
      .catch(() => {});
    api.get('/auth/me').then(({ data }) => {
      setUserRole(data.role || 'doctor');
      if (data.role === 'owner' || (data.role === 'admin' && data.owner_id)) {
        setIsClinicRole(true);
        api.get('/team/doctors').then(({ data: docs }) => {
          const list = Array.isArray(docs) ? docs : [];
          setClinicDoctors(list);
          // Auto-select first doctor for admin (no "all" view)
          if (data.role === 'admin' && list.length > 0 && !filterDoctorId) {
            setFilterDoctorId(list[0].id);
          }
        }).catch(() => {});
      }
      // Load rooms
      api.get('/rooms').then(({ data: r }) => setRooms(Array.isArray(r) ? r : [])).catch(() => {});
      // Load patients for autocomplete
      api.get('/patients').then(({ data: p }) => setPatients(Array.isArray(p) ? p : [])).catch(() => {});
    }).catch(() => {});
  }, [navigate, fetchAppointments]);

  useEffect(() => { fetchAppointments(); }, [filterDoctorId, fetchAppointments]);

  const handleCalendarRangeChange = useCallback((range) => {
    setCalendarRange((current) => (
      current?.dateFrom === range.dateFrom && current?.dateTo === range.dateTo
        ? current
        : range
    ));
  }, []);

  useEffect(() => {
    if (!calendarRange || viewMode !== 'calendar') return undefined;
    let active = true;
    const params = new URLSearchParams({
      date_from: calendarRange.dateFrom,
      date_to: calendarRange.dateTo,
    });
    if (filterDoctorId) params.set('doctor_id', filterDoctorId);

    setGoogleBusy([]);
    setGoogleBusyWarning('');
    api.get(`/appointments/google-busy?${params.toString()}`)
      .then(({ data }) => {
        if (!active) return;
        setGoogleBusy(Array.isArray(data?.busy) ? data.busy : []);
        setGoogleBusyWarning(data?.warnings?.[0] || '');
      })
      .catch(() => {
        if (!active) return;
        setGoogleBusy([]);
        setGoogleBusyWarning('Не удалось загрузить занятость Google Calendar');
      });

    return () => { active = false; };
  }, [calendarRange, filterDoctorId, viewMode]);

  // Fetch doctor schedule when filter changes (for clinic roles)
  useEffect(() => {
    if (isClinicRole && filterDoctorId) {
      fetchDoctorSchedule(filterDoctorId);
    }
  }, [isClinicRole, filterDoctorId, fetchDoctorSchedule]);

  const confirmAppt = async (id) => {
    try {
      const { data } = await api.put(`/appointments/${id}/confirm`, {});
      if (data.status === 'pending_doctor') {
        toast.success('Заявка передана специалисту');
      } else {
        toast.success('Запись подтверждена');
      }
      fetchAppointments();
    } catch { toast.error('Ошибка подтверждения'); }
  };

  const createAppt = async () => {
    const fmtsCheck = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
    const fmtCheck = fmtsCheck.find(f => f.id === newAppt.format_id);
    const isGroupAppt = fmtCheck?.is_group;

    if (isGroupAppt) {
      if (groupPatients.length === 0) { toast.error('Добавьте хотя бы одного участника'); return; }
    } else {
      if (!newAppt.patient_name) { toast.error('Укажите клиента'); return; }
    }
    if (!newAppt.date || !newAppt.time || !newAppt.format_id) {
      toast.error('Заполните формат, дату и время');
      return;
    }
    // Validate email if provided
    if (newAppt.patient_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newAppt.patient_email)) {
      toast.error('Неверный формат email');
      return;
    }
    // Validate time format HH:MM
    const timeParts = newAppt.time.split(':');
    if (timeParts.length !== 2) { toast.error('Неверный формат времени'); return; }
    const [hh, mm] = timeParts.map(Number);
    if (isNaN(hh) || isNaN(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
      toast.error('Неверное время (часы 0-23, минуты 0-59)');
      return;
    }
    setCreating(true);
    // For group: first participant = main patient, rest = additional
    const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
    const selFmt = fmts.find(f => f.id === newAppt.format_id);
    let finalPayload = { ...newAppt, time: `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}` };

    if (selFmt?.is_group && groupPatients.length > 0) {
      finalPayload.patient_name = groupPatients[0].name;
      finalPayload.patient_phone = groupPatients[0].phone || '';
      finalPayload.patient_email = groupPatients[0].email || '';
      finalPayload.patient_id = groupPatients[0]._id || '';
      finalPayload.additional_patients = groupPatients.slice(1).filter(p => p.name.trim());
    } else {
      finalPayload.patient_id = selectedPatient?.id && selectedPatient.id !== '_new'
        ? selectedPatient.id
        : '';
    }
    if (isClinicRole && filterDoctorId) {
      finalPayload.doctor_id = filterDoctorId;
    }
    // Working hours warning
    const wh = doctorSchedule?.working_hours || settings?.working_hours || {};
    if (newAppt.date) {
      const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
      const dayOfWeek = dayNames[new Date(newAppt.date).getDay()];
      const dayHours = wh[dayOfWeek];
      if (!dayHours?.enabled) toast.warning('Внимание: этот день отмечен как нерабочий');
      else if (dayHours.start && dayHours.end) {
        const timeStr = finalPayload.time;
        if (timeStr < dayHours.start || timeStr >= dayHours.end) toast.warning(`Время ${timeStr} за пределами рабочих часов`);
      }
    }
    try {
      await api.post('/appointments', finalPayload);
      toast.success('Запись создана');
      setCreateOpen(false);
      setNewAppt({ patient_name: '', patient_phone: '', patient_email: '', date: '', time: '', format_id: '', notes: '', room_id: '' });
      setGroupPatients([]);
      setGroupMode(false);
      fetchAppointments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    setCreating(false);
  };

  const cancelAppt = async (id) => {
    try {
      await api.put(`/appointments/${id}/cancel`, {});
      toast.success('Запись отменена');
      fetchAppointments();
    } catch { toast.error('Ошибка отмены'); }
  };

  const noShowAppt = async (id, who) => {
    try {
      await api.put(`/appointments/${id}/no-show?who=${who}`, {});
      toast.success(who === 'patient' ? 'Отмечена неявка клиента' : 'Отмечена неявка психолога');
      fetchAppointments();
    } catch { toast.error('Ошибка'); }
  };

  const createRecurring = async () => {
    if (!selectedAppt) return;
    try {
      const { data } = await api.post(`/appointments/recurring`,
        { appointment_id: selectedAppt.id, repeat_type: recurType, count: recurCount },
        
      );
      toast.success(`Создано ${data.created} повторяющихся записей`);
      setRecurOpen(false);
      setDetailOpen(false);
      fetchAppointments();
    } catch { toast.error('Ошибка создания'); }
  };

  const saveSessionNotes = useCallback(async (apptId, notes) => {
    if (!apptId) return;
    setNotesSaving(true);
    try {
      await api.put(`/appointments/${apptId}/session-notes`, { session_notes: notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
      setAppointments(prev => prev.map(a => a.id === apptId ? { ...a, session_notes: notes } : a));
    } catch { /* silent fail for autosave */ }
    setNotesSaving(false);
  }, []);

  const downloadConsentTemplate = async () => {
    try {
      const { data } = await api.get('/consents/legal-documents/current');
      if (!data.document) { toast.error('Актуальный шаблон согласия ещё не настроен'); return; }
      const response = await api.get(`/consents/legal-documents/${data.document.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `consent-${data.document.version}.pdf`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Не удалось скачать шаблон');
    }
  };

  const scheduleAutoSave = useCallback((notes) => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    notesTimerRef.current = setTimeout(() => {
      if (notesApptRef.current) saveSessionNotes(notesApptRef.current, notes);
    }, 1500);
  }, [saveSessionNotes]);

  const handleNotesChange = (e) => {
    const val = e.target.value;
    setSessionNotes(val);
    scheduleAutoSave(val);
  };

  const handleNotesBlur = () => {
    if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
    if (notesApptRef.current) saveSessionNotes(notesApptRef.current, sessionNotes);
  };

  const openDetail = (appt) => {
    // Save pending notes for previous appointment
    if (notesTimerRef.current) {
      clearTimeout(notesTimerRef.current);
      if (notesApptRef.current) saveSessionNotes(notesApptRef.current, sessionNotes);
    }
    setSelectedAppt(appt);
    setSessionNotes(appt.session_notes || '');
    setNotesSaved(false);
    notesApptRef.current = appt.id;
    setDetailOpen(true);
  };

  const createPatientFromAppt = async (appt) => {
    try {
      const { data } = await api.post(`/patients`, {
        name: appt.patient_name,
        phone: appt.patient_phone,
        email: appt.patient_email || '',
        notes: '',
      });
      // Link appointment to new patient
      await api.put(`/appointments/${appt.id}/link-patient?patient_id=${data.id}`, {});
      toast.success('Клиент создан и привязан');
      fetchAppointments();
      setDetailOpen(false);
    } catch { toast.error('Ошибка'); }
  };

  const rescheduleAppt = async () => {
    if (!selectedAppt || !newDate || !newTime) return;
    try {
      await api.put(
        `/appointments/${selectedAppt.id}/reschedule`,
        { date: format(newDate, 'yyyy-MM-dd'), time: newTime },
      );
      toast.success('Запись перенесена');
      setRescheduleOpen(false);
      setSelectedAppt(null);
      setNewDate(null);
      setNewTime('');
      fetchAppointments();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Ошибка переноса');
    }
  };

  // Drag-and-drop reschedule from calendar
  const handleCalendarReschedule = async (apptId, date, time) => {
    try {
      await api.put(
        `/appointments/${apptId}/reschedule`,
        { date, time },
        
      );
      toast.success('Запись перенесена');
      fetchAppointments();
    } catch { toast.error('Ошибка переноса'); }
  };

  // Click/drag on empty calendar slot → open dialog with time range
  const handleCellClick = ({ date, startTime, endTime, isWorking }) => {
    // Show choice dialog: create appointment or modify schedule
    setSlotDialogData({ date, startTime, endTime, isWorking });
    setSlotDialogOpen(true);
  };

  const handleAddOverride = async (type) => {
    if (!slotDialogData) return;
    const { date, startTime, endTime } = slotDialogData;
    try {
      await api.post(
        `/schedule-overrides`,
        { date, override_type: type, start: startTime, end: endTime, note: type === 'add' ? 'Добавлено из календаря' : 'Убрано из календаря' },
        
      );
      toast.success(type === 'add' ? 'Рабочие часы добавлены' : 'Часы убраны из расписания');
      setSlotDialogOpen(false);
      fetchAppointments();
    } catch { toast.error('Ошибка изменения расписания'); }
  };

  const filtered = appointments
    .filter((a) => filter === 'all' || a.status === filter)
    .filter((a) => !search || a.patient_name.toLowerCase().includes(search.toLowerCase()) || a.patient_phone.includes(search))
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.time.localeCompare(b.time);
    });

  const calendarAppointments = useMemo(
    () => [...subtractAppointmentsFromGoogleBusy(googleBusy, appointments), ...appointments],
    [appointments, googleBusy]
  );

  return (
    <div data-testid="appointments-page" className="flex min-h-screen" style={{ background: 'var(--app-bg)' }}>
      <Sidebar />
      <main className="flex-1 p-4 lg:p-8 pt-16 lg:pt-8 overflow-auto">
        <div className="max-w-full lg:max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6 animate-fade-in">
            <div>
              <h1 className="font-heading text-2xl lg:text-3xl text-stone-200">Записи</h1>
              {(doctorSchedule?.timezone || settings?.timezone) && (
                <p className="text-xs mt-1 text-stone-500">
                  Время: {timezoneLabel(doctorSchedule?.timezone || settings.timezone)}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isClinicRole && clinicDoctors.length > 0 && (
                <Select value={filterDoctorId} onValueChange={v => setFilterDoctorId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-stone-800/50 border-stone-800 text-stone-300 w-48 h-9">
                    <SelectValue placeholder="Выберите специалиста" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-800">
                    {userRole === 'owner' && (
                      <SelectItem value="all">Все специалисты</SelectItem>
                    )}
                    {clinicDoctors.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button data-testid="create-appointment-btn" size="sm" onClick={() => {
                setNewAppt({ patient_name: '', patient_phone: '', patient_email: '', date: '', time: '', format_id: '', notes: '', room_id: '' });
                setCreateStep(1); setSelectedPatient(null); setPatientSearch('');
                setGroupMode(false); setGroupPatients([]);
                setCreateOpen(true);
              }}
                className="bg-amber-700 hover:bg-amber-600 text-white">
                <UserPlus size={14} className="mr-1.5" /> Новая запись
              </Button>
              <div className="flex items-center gap-1 bg-stone-800/50 rounded-lg p-1 border border-stone-800">
              <Button
                data-testid="view-calendar"
                variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('calendar')}
                className={viewMode === 'calendar' ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'text-stone-500 hover:text-stone-200'}
              >
                <LayoutGrid size={14} className="mr-1.5" /> Календарь
              </Button>
              {isClinicRole && (
                <Button
                  data-testid="view-chessboard"
                  variant={viewMode === 'chessboard' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('chessboard')}
                  className={viewMode === 'chessboard' ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'text-stone-500 hover:text-stone-200'}
                >
                  <DoorOpen size={14} className="mr-1.5" /> Шахматка
                </Button>
              )}
              <Button
                data-testid="view-table"
                variant={viewMode === 'table' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('table')}
                className={viewMode === 'table' ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'text-stone-500 hover:text-stone-200'}
              >
                <List size={14} className="mr-1.5" /> Таблица
              </Button>
            </div>
            </div>
          </div>

          {telegramStatus?.available && (
            !telegramStatus.connected || telegramStatus.new_booking_notifications === false
          ) && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border p-3"
              style={{ borderColor: 'var(--app-border)', background: 'var(--app-card)' }}>
              <BellRing size={18} style={{ color: 'var(--app-accent)' }} aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium" style={{ color: 'var(--app-text)' }}>
                  Не пропускайте новые заявки
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                  {telegramStatus.connected
                    ? 'Telegram подключён, но уведомления о новых записях выключены.'
                    : 'Подключите Telegram, и новая запись сразу появится в личных сообщениях.'}
                </p>
              </div>
              <Button size="sm" variant="outline"
                onClick={() => navigate('/settings?tab=general#telegram-notifications')}
                style={{ borderColor: 'var(--app-border)', color: 'var(--app-text)' }}>
                Настроить
              </Button>
            </div>
          )}

          {/* Calendar View */}
          {viewMode === 'calendar' && (
            <>
              {googleBusyWarning && (
                <p className="mb-3 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  {googleBusyWarning}. Записи ProPraxis показаны без изменений.
                </p>
              )}
              <CalendarView
                appointments={calendarAppointments}
                workingHours={doctorSchedule?.working_hours || settings?.working_hours}
                overrides={doctorSchedule?.overrides || overrides}
                cyclicSchedule={settings?.cyclic_schedule}
                onAppointmentClick={(appt) => openDetail(appt)}
                onReschedule={handleCalendarReschedule}
                onCellClick={handleCellClick}
                onRangeChange={handleCalendarRangeChange}
              />
            </>
          )}

          {/* Chessboard View (Rooms × Time) */}
          {viewMode === 'chessboard' && (
            <ChessboardView onAppointmentClick={(appt) => openDetail(appt)} />
          )}

          {/* Table View */}
          {viewMode === 'table' && (
            <>

          {/* Filters */}
          <Card className="bg-stone-900 border-stone-800 mb-6 animate-fade-in delay-1">
            <CardContent className="p-4 flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-600" />
                <Input
                  data-testid="search-input"
                  placeholder="Поиск по имени или телефону..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600"
                />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="status-filter" className="w-[180px] bg-stone-950/50 border-stone-800 text-stone-300">
                  <SelectValue placeholder="Статус" />
                </SelectTrigger>
                <SelectContent className="bg-stone-900 border-stone-800">
                  <SelectItem value="all">Все статусы</SelectItem>
                  <SelectItem value="pending_admin">Ожидают админа</SelectItem>
                  <SelectItem value="pending_doctor">Ожидают врача</SelectItem>
                  <SelectItem value="pending">Ожидают</SelectItem>
                  <SelectItem value="confirmed">Подтверждены</SelectItem>
                  <SelectItem value="cancelled">Отменены</SelectItem>
                  <SelectItem value="no_show_patient">Неявка клиента</SelectItem>
                  <SelectItem value="no_show_doctor">Неявка психолога</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="bg-stone-900 border-stone-800 animate-fade-in delay-2">
            <CardContent className="p-0">
              {loading ? (
                <p className="text-stone-500 text-center py-8">Загрузка...</p>
              ) : filtered.length === 0 ? (
                <p className="text-stone-600 text-center py-8">Нет записей</p>
              ) : (
                <div className="overflow-x-auto"><Table>
                  <TableHeader>
                    <TableRow className="border-stone-800 hover:bg-transparent">
                      <TableHead className="text-stone-500">Дата</TableHead>
                      <TableHead className="text-stone-500">Время</TableHead>
                      <TableHead className="text-stone-500">Клиент</TableHead>
                      <TableHead className="text-stone-500">Телефон</TableHead>
                      <TableHead className="text-stone-500">Формат</TableHead>
                      {rooms.length > 0 && <TableHead className="text-stone-500">Кабинет</TableHead>}
                      <TableHead className="text-stone-500">Статус</TableHead>
                      <TableHead className="text-stone-500 text-right">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((appt) => (
                      <TableRow key={appt.id} data-testid={`appt-row-${appt.id}`} className="border-stone-800/50 hover:bg-stone-800/30">
                        <TableCell className="text-stone-300 text-sm">
                          {(() => { try { return format(parseISO(appt.date), 'd MMM', { locale: ru }); } catch { return appt.date; } })()}
                        </TableCell>
                        <TableCell className="text-amber-500 font-medium text-sm">{appt.time}</TableCell>
                        <TableCell className="text-stone-200 text-sm font-medium">
                          {appt.is_group
                            ? <span>{[appt.patient_name, ...(appt.additional_patients || []).map(p => p.name)].join(', ')} <span className="text-violet-400 text-[10px]">({1 + (appt.additional_patients || []).length})</span></span>
                            : appt.patient_name}
                          {!appt.matched_patient_id && !appt.is_group && <span className="ml-1.5 text-amber-500 text-[10px]" title="Нет в базе">*</span>}
                        </TableCell>
                        <TableCell className="text-stone-400 text-sm">{appt.patient_phone}</TableCell>
                        <TableCell className="text-stone-400 text-sm">{appt.format_name}</TableCell>
                        {rooms.length > 0 && (
                          <TableCell className="text-sm">
                            {appt.room_id ? (
                              <span className="text-teal-400 flex items-center gap-1"><DoorOpen size={12} />{rooms.find(r => r.id === appt.room_id)?.name || '—'}</span>
                            ) : <span className="text-stone-600">—</span>}
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className={statusConfig[appt.status]?.className + ' text-xs'}>
                            {statusConfig[appt.status]?.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {['pending_admin', 'pending_doctor', 'pending'].includes(appt.status) && (
                              <>
                                <Button
                                  data-testid={`confirm-btn-${appt.id}`}
                                  variant="ghost" size="sm"
                                  onClick={() => confirmAppt(appt.id)}
                                  className="text-green-500 hover:text-green-400 hover:bg-green-900/20 h-8 w-8 p-0"
                                  title={appt.status === 'pending_admin' ? 'Одобрить (→ врачу)' : 'Подтвердить'}
                                >
                                  <CheckCircle size={16} />
                                </Button>
                                <Button
                                  data-testid={`cancel-btn-${appt.id}`}
                                  variant="ghost" size="sm"
                                  onClick={() => cancelAppt(appt.id)}
                                  className="text-red-500 hover:text-red-400 hover:bg-red-900/20 h-8 w-8 p-0"
                                  title="Отклонить"
                                >
                                  <XCircle size={16} />
                                </Button>
                              </>
                            )}
                            {appt.status === 'confirmed' && (
                              <>
                                <Button
                                  data-testid={`noshow-patient-${appt.id}`}
                                  variant="ghost" size="sm"
                                  onClick={() => noShowAppt(appt.id, 'patient')}
                                  className="text-violet-500 hover:text-violet-400 hover:bg-violet-900/20 h-8 w-8 p-0"
                                  title="Неявка клиента"
                                >
                                  <UserX size={16} />
                                </Button>
                                <Button
                                  data-testid={`noshow-doctor-${appt.id}`}
                                  variant="ghost" size="sm"
                                  onClick={() => noShowAppt(appt.id, 'doctor')}
                                  className="text-blue-500 hover:text-blue-400 hover:bg-blue-900/20 h-8 w-8 p-0"
                                  title="Неявка психолога"
                                >
                                  <UserMinus size={16} />
                                </Button>
                              </>
                            )}
                            {!['cancelled', 'no_show_patient', 'no_show_doctor'].includes(appt.status) && (
                              <>
                                <Button
                                  data-testid={`reschedule-btn-${appt.id}`}
                                  variant="ghost" size="sm"
                                  onClick={() => {
                                    setSelectedAppt(appt);
                                    try { setNewDate(parseISO(appt.date)); } catch { setNewDate(null); }
                                    setNewTime(appt.time || '');
                                    setRescheduleOpen(true);
                                  }}
                                  className="text-stone-500 hover:text-amber-500 hover:bg-stone-800 h-8 w-8 p-0"
                                  title="Перенести"
                                >
                                  <CalendarDays size={16} />
                                </Button>
                                <Button
                                  data-testid={`recurring-btn-${appt.id}`}
                                  variant="ghost" size="sm"
                                  onClick={() => { setSelectedAppt(appt); setRecurOpen(true); }}
                                  className="text-stone-500 hover:text-stone-300 hover:bg-stone-800 h-8 w-8 p-0"
                                  title="Повторить"
                                >
                                  <Repeat size={16} />
                                </Button>
                              </>
                            )}
                            {(appt.status === 'paid' || appt.payment_status === 'paid') && (
                              <Button variant="ghost" size="sm" title="Квитанция PDF"
                                onClick={async () => {
                                  try {
                                    const resp = await api.get(`/appointments/${appt.id}/receipt`, { responseType: 'blob' });
                                    const url = URL.createObjectURL(resp.data);
                                    const a = document.createElement('a'); a.href = url; a.download = `receipt_${appt.id}.pdf`; a.click();
                                    URL.revokeObjectURL(url);
                                  } catch { toast.error('Ошибка'); }
                                }}
                                className="text-green-600 hover:text-green-400 hover:bg-green-900/20 h-8 w-8 p-0">
                                <FileText size={16} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table></div>
              )}
            </CardContent>
          </Card>
          </>
          )}
        </div>
      </main>

      {/* Create Appointment Dialog — Wide Layout */}
      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (open && patients.length === 0) { api.get('/patients').then(({ data }) => setPatients(Array.isArray(data) ? data : [])).catch(() => {}); }
        if (!open) { setSelectedPatient(null); setPatientSearch(''); setNewApptSlots([]); }
      }}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="create-appt-desc">
          <DialogHeader>
            <DialogTitle className="text-stone-200">
              {doctorSchedule ? `Новая запись — ${doctorSchedule.doctor_name}` : 'Новая запись'}
            </DialogTitle>
          </DialogHeader>
          <p id="create-appt-desc" className="sr-only">Создание записи</p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* LEFT COLUMN: Client + Format + Details */}
            <div className="space-y-3">
              {/* Client picker — single kartoteka for main + group */}
              <div>
                <label className="text-stone-400 text-[10px] uppercase tracking-wider mb-1.5 block">
                  {(() => {
                    const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
                    const selFmt = fmts.find(f => f.id === newAppt.format_id);
                    return selFmt?.is_group ? 'Участники' : 'Клиент';
                  })()}
                </label>
                {/* Selected chips — all equal for group */}
                {(() => {
                  const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
                  const selFmt = fmts.find(f => f.id === newAppt.format_id);
                  const isGroup = selFmt?.is_group;
                  if (isGroup) {
                    return groupPatients.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {groupPatients.map((gp, i) => (
                          <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-900/15 border border-amber-900/25 text-xs">
                            <span className="text-amber-400">{gp.name}</span>
                            <button onClick={() => setGroupPatients(groupPatients.filter((_, j) => j !== i))}
                              className="text-stone-600 hover:text-red-400 ml-0.5"><X size={10} /></button>
                          </div>
                        ))}
                      </div>
                    );
                  }
                  return selectedPatient && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-900/15 border border-amber-900/25 text-xs">
                        <span className="text-amber-400">{selectedPatient.name}</span>
                        <button onClick={() => { setSelectedPatient(null); setPatientSearch(''); setNewAppt(a => ({...a, patient_name: '', patient_phone: '', patient_email: ''})); }}
                          className="text-stone-600 hover:text-red-400 ml-0.5"><X size={10} /></button>
                      </div>
                    </div>
                  );
                })()}
                {/* Search */}
                <Input data-testid="new-appt-name"
                  value={patientSearch}
                  onChange={e => setPatientSearch(e.target.value)}
                  placeholder="Поиск по имени или телефону..." className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600 mb-1.5" />
                {/* Kartoteka list — always visible */}
                {(() => {
                  const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
                  const selFmt = fmts.find(f => f.id === newAppt.format_id);
                  const isGroup = selFmt?.is_group;
                  const maxP = selFmt?.max_participants || 10;
                  const allSelected = new Set([
                    ...(selectedPatient ? [selectedPatient.id] : []),
                    ...groupPatients.filter(g => g._id).map(g => g._id),
                  ]);
                  const canAddMore = isGroup ? (groupPatients.length < maxP) : !selectedPatient;
                  if (!canAddMore) return null;
                  return (
                    <div className="max-h-36 overflow-y-auto rounded-lg border border-stone-800/50 bg-stone-950/20">
                      {patients.filter(p =>
                        !allSelected.has(p.id) && (
                          !patientSearch ||
                          p.name.toLowerCase().includes(patientSearch.toLowerCase()) ||
                          (p.phone || '').includes(patientSearch)
                        )
                      ).map(p => (
                        <button key={p.id} className="w-full text-left px-3 py-2 hover:bg-stone-800/50 transition-colors border-b border-stone-800/30 last:border-0"
                          onClick={() => {
                            if (isGroup) {
                              setGroupPatients(prev => [...prev, {name: p.name, phone: p.phone || '', email: p.email || '', _id: p.id}]);
                            } else {
                              setSelectedPatient(p);
                              setNewAppt(a => ({...a, patient_name: p.name, patient_phone: p.phone || '', patient_email: p.email || ''}));
                            }
                            setPatientSearch('');
                          }}>
                          <span className="text-stone-200 text-sm">{p.name}</span>
                          {p.phone && <span className="text-stone-600 text-xs ml-2">{p.phone}</span>}
                        </button>
                      ))}
                      {patients.length === 0 && <p className="text-stone-600 text-xs text-center py-3">Картотека пуста</p>}
                    </div>
                  );
                })()}
                {/* New patient (non-group only) */}
                {!selectedPatient && patientSearch && (() => {
                  const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
                  const selFmt = fmts.find(f => f.id === newAppt.format_id);
                  if (selFmt?.is_group) return null;
                  return (
                    <button onClick={() => { setNewAppt(a => ({...a, patient_name: patientSearch})); setPatientSearch(''); setSelectedPatient({id: '_new', name: patientSearch}); }}
                      className="w-full text-left px-3 py-2 mt-1 rounded-lg bg-amber-900/10 border border-amber-900/20 text-amber-400 text-xs hover:bg-amber-900/20">
                      + Создать «{patientSearch}» как нового клиента
                    </button>
                  );
                })()}
              </div>

              {/* Format cards */}
              <div>
                <label className="text-stone-400 text-[10px] uppercase tracking-wider mb-1.5 block">Формат</label>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(doctorSchedule?.meeting_formats || settings?.meeting_formats || []).map(f => (
                    <button key={f.id} onClick={() => {
                      setNewAppt(a => ({...a, format_id: f.id}));
                      // Refetch slots if date already selected
                      if (newAppt.date) {
                        const pid = filterDoctorId || settings?.psychologist_id || '';
                        api.get('/public/available-slots', { params: { date: newAppt.date, format_id: f.id, psychologist_id: pid }})
                          .then(({ data }) => setNewApptSlots(Array.isArray(data) ? data : []))
                          .catch(() => setNewApptSlots([]));
                      }
                    }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all border ${
                        newAppt.format_id === f.id ? 'bg-amber-700/15 border-amber-700/50 text-amber-400' : 'bg-stone-950/30 border-stone-800/50 text-stone-300 hover:border-stone-700'
                      }`}>
                      <span className="font-medium">{f.name}</span>
                      <span className="text-stone-500 text-xs ml-2">{f.duration} мин.{f.price ? ` · ${formatMoney(f.price, f.currency || settings?.currency)}` : ''}</span>
                      {f.is_group && <span className="text-violet-400 text-[10px] ml-1">· группа</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Room + Notes */}
              {rooms.length > 0 && (
                <Select value={newAppt.room_id} onValueChange={v => setNewAppt(a => ({...a, room_id: v === '_none' ? '' : v}))}>
                  <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200 h-9">
                    <SelectValue placeholder="Кабинет" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-800">
                    <SelectItem value="_none">Без кабинета</SelectItem>
                    {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Textarea value={newAppt.notes} onChange={e => setNewAppt(a => ({...a, notes: e.target.value}))}
                placeholder="Примечание" rows={2} className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600 text-sm resize-none" />
            </div>

            {/* RIGHT COLUMN: Calendar + Time Slots */}
            <div className="space-y-3">
              <label className="text-stone-400 text-[10px] uppercase tracking-wider mb-1.5 block">Дата и время</label>
              <div className="rounded-xl p-2 bg-stone-950/30 border border-stone-800/50">
                <Calendar
                  mode="single"
                  selected={newAppt.date ? new Date(newAppt.date + 'T00:00') : undefined}
                  onSelect={d => {
                    if (!d) return;
                    const ds = format(d, 'yyyy-MM-dd');
                    setNewAppt(a => ({...a, date: ds, time: ''}));
                    // Fetch available slots
                    if (newAppt.format_id) {
                      const pid = filterDoctorId || settings?.psychologist_id || '';
                      api.get('/public/available-slots', { params: { date: ds, format_id: newAppt.format_id, psychologist_id: pid }})
                        .then(({ data }) => setNewApptSlots(Array.isArray(data) ? data : []))
                        .catch(() => setNewApptSlots([]));
                    }
                  }}
                  locale={ru}
                  disabled={d => { const t = new Date(); t.setHours(0,0,0,0); return d < t; }}
                  className="text-stone-200"
                  classNames={{
                    day_selected: "bg-amber-700 text-white hover:bg-amber-600",
                    day_today: "text-amber-500 font-bold",
                  }}
                />
              </div>
              {/* Time slots grid */}
              {newAppt.date && (
                <div>
                  <p className="text-stone-500 text-[10px] mb-1.5">{newAppt.date.split('-').reverse().join('.')} — доступные слоты:</p>
                  {newApptSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-1.5">
                      {newApptSlots.map(t => (
                        <button key={t} onClick={() => setNewAppt(a => ({...a, time: t}))}
                          className={`py-1.5 rounded-md text-xs font-medium transition-all text-center ${
                            newAppt.time === t ? 'bg-amber-700 text-white' : 'bg-stone-950/50 border border-stone-800/50 text-stone-400 hover:border-amber-700/40 hover:text-amber-400'
                          }`}>{t}</button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-stone-600 text-xs py-2">Нет свободных слотов{!newAppt.format_id && ' (выберите формат)'}</p>
                  )}
                  <div className="mt-2">
                    <label className="text-stone-600 text-[10px] mb-0.5 block">Или введите вручную:</label>
                    <Input type="time" value={newAppt.time} onChange={e => setNewAppt(a => ({...a, time: e.target.value}))}
                      className="bg-stone-950/50 border-stone-800 text-stone-200 w-32 h-8 text-sm" />
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="mt-4 pt-3 border-t border-stone-800/50">
            <Button variant="ghost" onClick={() => setCreateOpen(false)} className="text-stone-400">Отмена</Button>
            <Button data-testid="submit-new-appt" onClick={createAppt} disabled={creating}
              className="bg-amber-700 hover:bg-amber-600 text-white">
              {creating ? 'Создание...' : 'Создать запись'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Appointment Detail Dialog (from Calendar) */}
      <Dialog open={detailOpen} onOpenChange={(open) => {
        if (!open && notesApptRef.current) {
          if (notesTimerRef.current) clearTimeout(notesTimerRef.current);
          saveSessionNotes(notesApptRef.current, sessionNotes);
          notesApptRef.current = null;
        }
        setDetailOpen(open);
      }}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Запись</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-3">
              <div className="p-4 rounded-lg bg-stone-950/40 border border-stone-800/50 space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-stone-200 font-medium">{selectedAppt.patient_name}</p>
                  <Badge className={statusConfig[selectedAppt.status]?.className + ' text-xs'}>
                    {statusConfig[selectedAppt.status]?.label}
                  </Badge>
                </div>
                <p className="text-amber-500 text-sm">{selectedAppt.date} {selectedAppt.time} / {selectedAppt.format_name} ({selectedAppt.duration} мин)</p>
                {selectedAppt.contact_value ? (
                  <p className="text-stone-500 text-sm">
                    {{ telegram: 'Telegram', email: 'Email', phone: 'Телефон' }[selectedAppt.contact_method] || 'Контакт'}: {selectedAppt.contact_value}
                  </p>
                ) : (
                  <>
                    {selectedAppt.patient_phone && <p className="text-stone-500 text-sm">Телефон: {selectedAppt.patient_phone}</p>}
                    {selectedAppt.patient_email && <p className="text-stone-500 text-sm">Email: {selectedAppt.patient_email}</p>}
                  </>
                )}
                {selectedAppt.is_group && selectedAppt.additional_patients?.length > 0 && (
                  <div className="mt-2 p-2 rounded bg-violet-900/10 border border-violet-900/20">
                    <p className="text-violet-400 text-xs font-medium mb-1">
                      Группа ({1 + selectedAppt.additional_patients.length} чел.)
                      {(() => {
                        const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
                        const fmt = fmts.find(f => f.id === selectedAppt.format_id);
                        if (!fmt?.price) return null;
                        const count = 1 + selectedAppt.additional_patients.length;
                        if (fmt.group_price_type === 'per_person') {
                          return <span className="text-stone-500 ml-1">· {formatMoney(fmt.price, fmt.currency || settings?.currency)} × {count} = {formatMoney(fmt.price * count, fmt.currency || settings?.currency)}</span>;
                        }
                        return <span className="text-stone-500 ml-1">· {formatMoney(fmt.price, fmt.currency || settings?.currency)} (общая)</span>;
                      })()}
                    </p>
                    {selectedAppt.additional_patients.map((p, i) => (
                      <p key={i} className="text-stone-400 text-xs">{p.name}{p.phone ? ` · ${p.phone}` : ''}</p>
                    ))}
                  </div>
                )}
                {selectedAppt.notes && <p className="text-stone-600 text-xs">{selectedAppt.notes}</p>}
                {!selectedAppt.matched_patient_id && (
                  <div className="mt-2 p-3 rounded-lg bg-amber-950/20 border border-amber-900/30 space-y-2">
                    <p className="text-amber-400 text-xs font-medium">Клиент не привязан к картотеке</p>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => createPatientFromAppt(selectedAppt)} className="bg-amber-700 hover:bg-amber-600 text-white h-7 text-xs">
                        <UserPlus size={12} className="mr-1" /> Создать нового
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setLinkPatientOpen(true)}
                        className="border-stone-700 text-stone-300 hover:text-amber-400 hover:border-amber-700 h-7 text-xs">
                        <Search size={12} className="mr-1" /> Привязать к существующему
                      </Button>
                    </div>
                  </div>
                )}
              </div>
              {/* Session Notes — Doctor only */}
              {userRole === 'doctor' && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <FileText size={14} className="text-amber-500" />
                  <label className="text-stone-400 text-sm font-medium">Заметки к сеансу</label>
                  {notesSaving && <span className="text-stone-600 text-xs ml-auto">Сохраняю...</span>}
                  {notesSaved && !notesSaving && <span className="text-green-500 text-xs ml-auto">Сохранено</span>}
                </div>
                {selectedAppt.sensitive_sections_locked ? (
                  <div className="rounded-lg border border-amber-900/50 bg-amber-950/20 p-3 text-xs text-amber-200">
                    <p>Для хранения чувствительной информации необходимо подтвердить наличие соответствующего согласия клиента</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" size="sm" onClick={() => navigate(`/patients?client=${selectedAppt.matched_patient_id || ''}`)} className="bg-amber-700 text-white hover:bg-amber-600">Подтвердить согласие</Button>
                      <Button type="button" size="sm" variant="outline" onClick={downloadConsentTemplate} className="border-stone-700 text-stone-300">Скачать шаблон</Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setDetailOpen(false)} className="text-stone-400">Отмена</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Textarea
                      data-testid="session-notes-input"
                      value={sessionNotes}
                      onChange={handleNotesChange}
                      onBlur={handleNotesBlur}
                      placeholder="Заметки психолога к этому сеансу..."
                      rows={3}
                      className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-700 resize-none text-sm"
                    />
                    <p className="text-stone-700 text-[10px]">Автосохранение при вводе</p>
                    {/* ICD-10 field (MEDICAL only) */}
                    <ICD10Field apptId={selectedAppt.id} currentCode={selectedAppt.icd10_code || ''} />
                  </>
                )}
              </div>
              )}

              {/* Receipt button for paid appointments */}
              {(selectedAppt.status === 'paid' || selectedAppt.payment_status === 'paid') && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-900/10 border border-green-900/20">
                  <CheckCircle size={14} className="text-green-500" />
                  <span className="text-green-400 text-sm flex-1">Оплачено{selectedAppt.payment_amount ? ` — ${formatMoney(selectedAppt.payment_amount, selectedAppt.payment_currency || selectedAppt.currency || settings?.currency)}` : ''}</span>
                  <Button size="sm" onClick={async () => {
                    try {
                      const resp = await api.get(`/appointments/${selectedAppt.id}/receipt`, { responseType: 'blob' });
                      const url = URL.createObjectURL(resp.data);
                      const a = document.createElement('a'); a.href = url; a.download = `receipt_${selectedAppt.id}.pdf`; a.click();
                      URL.revokeObjectURL(url);
                    } catch { toast.error('Ошибка генерации квитанции'); }
                  }} className="bg-green-800 hover:bg-green-700 text-white text-xs">
                    <FileText size={12} className="mr-1" /> Квитанция PDF
                  </Button>
                </div>
              )}

              <div className="flex gap-2 justify-end flex-wrap">
                {['pending_admin', 'pending_doctor', 'pending'].includes(selectedAppt.status) && (
                  <>
                    <Button size="sm" onClick={() => { confirmAppt(selectedAppt.id); setDetailOpen(false); }} className="bg-green-800 hover:bg-green-700 text-white">
                      <CheckCircle size={14} className="mr-1" /> {selectedAppt.status === 'pending_admin' ? 'Одобрить' : 'Подтвердить'}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => { cancelAppt(selectedAppt.id); setDetailOpen(false); }} className="bg-red-800 hover:bg-red-700">
                      <XCircle size={14} className="mr-1" /> Отклонить
                    </Button>
                  </>
                )}
                {selectedAppt.status === 'confirmed' && (
                  <>
                    <Button size="sm" onClick={() => { noShowAppt(selectedAppt.id, 'patient'); setDetailOpen(false); }} className="bg-violet-800 hover:bg-violet-700 text-white">
                      <UserX size={14} className="mr-1" /> Неявка клиента
                    </Button>
                    <Button size="sm" onClick={() => { noShowAppt(selectedAppt.id, 'doctor'); setDetailOpen(false); }} className="bg-blue-800 hover:bg-blue-700 text-white">
                      <UserMinus size={14} className="mr-1" /> Неявка психолога
                    </Button>
                  </>
                )}
                {!['cancelled', 'no_show_patient', 'no_show_doctor', 'paid'].includes(selectedAppt.status) && (
                  <>
                    <Button size="sm" variant="secondary" onClick={() => {
                      setEditAppt({ format_id: selectedAppt.format_id, notes: selectedAppt.notes || '' });
                      // Populate group participants for editing
                      const allParticipants = [
                        { name: selectedAppt.patient_name, phone: selectedAppt.patient_phone || '', email: selectedAppt.patient_email || '', _id: selectedAppt.matched_patient_id || '' },
                        ...(selectedAppt.additional_patients || []).map(p => ({ ...p, _id: p._id || '' })),
                      ];
                      setEditGroupPatients(selectedAppt.is_group ? allParticipants : []);
                      setEditGroupSearch('');
                      setEditApptOpen(true);
                    }} className="bg-stone-800 text-stone-300 hover:bg-stone-700">
                      <Pencil size={14} className="mr-1" /> Изменить
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => {
                      try { setNewDate(parseISO(selectedAppt.date)); } catch { setNewDate(null); }
                      setNewTime(selectedAppt.time || '');
                      setDetailOpen(false);
                      setRescheduleOpen(true);
                    }} className="bg-stone-800 text-stone-300 hover:bg-stone-700">
                      <CalendarDays size={14} className="mr-1" /> Перенести
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setRecurOpen(true)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">
                      <Repeat size={14} className="mr-1" /> Повторить
                    </Button>
                    <Button size="sm" variant="destructive" onClick={async () => {
                      if (!window.confirm(`Удалить запись ${selectedAppt.patient_name} ${selectedAppt.date} ${selectedAppt.time}?`)) return;
                      try {
                        await api.delete(`/appointments/${selectedAppt.id}`);
                        toast.success('Запись удалена');
                        setDetailOpen(false);
                        fetchAppointments();
                      } catch { toast.error('Ошибка удаления'); }
                    }} className="bg-red-900/50 hover:bg-red-800 text-red-300">
                      <Trash2 size={14} className="mr-1" /> Удалить
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={(open) => { setRescheduleOpen(open); if (!open) { setNewDate(null); setNewTime(''); } }}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Перенести запись</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-4">
              <p className="text-stone-400 text-sm">
                {selectedAppt.patient_name} / {selectedAppt.format_name}
              </p>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Новая дата</label>
                <Calendar
                  data-testid="reschedule-calendar"
                  mode="single"
                  selected={newDate}
                  onSelect={setNewDate}
                  locale={ru}
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                  className="rounded-lg border border-stone-800 bg-stone-950/50"
                />
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Новое время</label>
                <Input
                  data-testid="reschedule-time-input"
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="bg-stone-950/50 border-stone-800 text-stone-200"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRescheduleOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">
              Отмена
            </Button>
            <Button
              data-testid="reschedule-confirm-btn"
              onClick={rescheduleAppt}
              disabled={!newDate || !newTime}
              className="bg-amber-700 hover:bg-amber-600 text-white"
            >
              Перенести
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Dialog */}
      <Dialog open={recurOpen} onOpenChange={setRecurOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Повторяющаяся запись</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-4">
              <p className="text-stone-400 text-sm">{selectedAppt.patient_name} / {selectedAppt.format_name}</p>
              <p className="text-stone-500 text-xs">{selectedAppt.date} {selectedAppt.time}</p>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Периодичность</label>
                <Select value={recurType} onValueChange={setRecurType}>
                  <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-800">
                    <SelectItem value="weekly">Каждую неделю</SelectItem>
                    <SelectItem value="biweekly">Раз в 2 недели</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-stone-400 text-sm font-medium mb-2 block">Количество записей</label>
                <Input data-testid="recurring-count" type="number" min={1} max={52} value={recurCount}
                  onChange={e => setRecurCount(Math.max(1, parseInt(e.target.value) || 1))}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRecurOpen(false)} className="bg-stone-800 text-stone-300 hover:bg-stone-700">Отмена</Button>
            <Button data-testid="recurring-create-btn" onClick={createRecurring} className="bg-amber-700 hover:bg-amber-600 text-white">
              <Repeat size={14} className="mr-1" /> Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link to Existing Patient Dialog */}
      <Dialog open={linkPatientOpen} onOpenChange={o => { setLinkPatientOpen(o); if (!o) setLinkPatientSearch(''); }}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Привязать к клиенту</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-3">
              <p className="text-stone-500 text-xs">Запись: <span className="text-stone-300">{selectedAppt.patient_name}</span> ({selectedAppt.patient_phone})</p>
              <Input
                autoFocus
                value={linkPatientSearch}
                onChange={e => setLinkPatientSearch(e.target.value)}
                placeholder="Поиск по имени или телефону..."
                className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600" />
              <div className="max-h-52 overflow-y-auto space-y-1">
                {patients.filter(p =>
                  !linkPatientSearch ||
                  p.name.toLowerCase().includes(linkPatientSearch.toLowerCase()) ||
                  (p.phone || '').includes(linkPatientSearch)
                ).slice(0, 15).map(p => (
                  <button key={p.id}
                    onClick={async () => {
                      try {
                        await api.put(`/appointments/${selectedAppt.id}/link-patient?patient_id=${p.id}`, {});
                        toast.success(`Привязано к ${p.name}`);
                        setLinkPatientOpen(false);
                        setDetailOpen(false);
                        fetchAppointments();
                      } catch { toast.error('Ошибка привязки'); }
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-stone-800/50 transition-colors flex items-center justify-between border border-transparent hover:border-stone-700">
                    <div>
                      <p className="text-stone-200 text-sm">{p.name}</p>
                      <p className="text-stone-600 text-xs">{p.phone}{p.email ? ` · ${p.email}` : ''}</p>
                    </div>
                    <UserPlus size={14} className="text-stone-600 shrink-0" />
                  </button>
                ))}
                {patients.filter(p =>
                  linkPatientSearch &&
                  (p.name.toLowerCase().includes(linkPatientSearch.toLowerCase()) || (p.phone || '').includes(linkPatientSearch))
                ).length === 0 && linkPatientSearch && (
                  <p className="text-stone-600 text-xs text-center py-4">Не найдено</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Appointment Dialog */}
      <Dialog open={editApptOpen} onOpenChange={o => { setEditApptOpen(o); if (!o) setEditGroupSearch(''); }}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200">Изменить запись</DialogTitle>
          </DialogHeader>
          {selectedAppt && (
            <div className="space-y-3">
              <p className="text-stone-500 text-xs">{selectedAppt.date} {selectedAppt.time}</p>
              <div>
                <label className="text-stone-500 text-[10px] mb-1 block">Формат</label>
                <Select value={editAppt.format_id} onValueChange={v => setEditAppt(a => ({...a, format_id: v}))}>
                  <SelectTrigger className="bg-stone-950/50 border-stone-800 text-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-800">
                    {(doctorSchedule?.meeting_formats || settings?.meeting_formats || []).map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name} ({f.duration} мин.){f.is_group ? ' · группа' : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* Group participants editor */}
              {(() => {
                const fmts = doctorSchedule?.meeting_formats || settings?.meeting_formats || [];
                const selFmt = fmts.find(f => f.id === editAppt.format_id);
                if (!selFmt?.is_group && !selectedAppt.is_group) return null;
                const maxP = selFmt?.max_participants || 10;
                const selectedIds = new Set(editGroupPatients.filter(g => g._id).map(g => g._id));
                return (
                  <div className="space-y-2">
                    <label className="text-stone-400 text-[10px] uppercase tracking-wider block">Участники ({editGroupPatients.length}/{maxP})</label>
                    <div className="flex flex-wrap gap-1.5">
                      {editGroupPatients.map((gp, i) => (
                        <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-900/15 border border-amber-900/25 text-xs">
                          <span className="text-amber-400">{gp.name}</span>
                          <button onClick={() => setEditGroupPatients(editGroupPatients.filter((_, j) => j !== i))}
                            className="text-stone-600 hover:text-red-400 ml-0.5"><X size={10} /></button>
                        </div>
                      ))}
                    </div>
                    {editGroupPatients.length < maxP && (
                      <>
                        <Input value={editGroupSearch} onChange={e => setEditGroupSearch(e.target.value)}
                          placeholder="Добавить участника..." className="bg-stone-950/50 border-stone-800 text-stone-200 placeholder:text-stone-600 text-xs h-8" />
                        <div className="max-h-28 overflow-y-auto rounded border border-stone-800/40 bg-stone-950/20">
                          {patients.filter(p =>
                            !selectedIds.has(p.id) && (
                              !editGroupSearch ||
                              p.name.toLowerCase().includes(editGroupSearch.toLowerCase()) ||
                              (p.phone || '').includes(editGroupSearch)
                            )
                          ).map(p => (
                            <button key={p.id} className="w-full text-left px-2 py-1.5 hover:bg-stone-800/50 text-xs border-b border-stone-800/20 last:border-0"
                              onClick={() => { setEditGroupPatients(prev => [...prev, {name: p.name, phone: p.phone || '', email: p.email || '', _id: p.id}]); setEditGroupSearch(''); }}>
                              <span className="text-stone-200">{p.name}</span>
                              {p.phone && <span className="text-stone-600 ml-1.5">{p.phone}</span>}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
              <div>
                <label className="text-stone-500 text-[10px] mb-1 block">Примечание</label>
                <Input value={editAppt.notes} onChange={e => setEditAppt(a => ({...a, notes: e.target.value}))}
                  className="bg-stone-950/50 border-stone-800 text-stone-200" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditApptOpen(false)} className="bg-stone-800 text-stone-300">Отмена</Button>
            <Button onClick={async () => {
              try {
                const payload = { ...editAppt };
                // For group: first = patient_name, rest = additional_patients
                if (editGroupPatients.length > 0) {
                  payload.patient_name = editGroupPatients[0].name;
                  payload.patient_phone = editGroupPatients[0].phone || '';
                  payload.patient_email = editGroupPatients[0].email || '';
                  payload.additional_patients = editGroupPatients.slice(1);
                  payload.is_group = true;
                }
                await api.put(`/appointments/${selectedAppt.id}/edit`, payload);
                toast.success('Запись обновлена');
                setEditApptOpen(false);
                setDetailOpen(false);
                fetchAppointments();
              } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
            }} className="bg-amber-700 hover:bg-amber-600 text-white">Сохранить</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slot Action Dialog — Create Appointment or Modify Schedule */}
      <Dialog open={slotDialogOpen} onOpenChange={setSlotDialogOpen}>
        <DialogContent className="bg-stone-900 border-stone-800 text-stone-200 max-w-xs">
          <DialogHeader>
            <DialogTitle className="font-heading text-stone-200 text-base">Что сделать?</DialogTitle>
          </DialogHeader>
          {slotDialogData && (
            <div className="space-y-3">
              <p className="text-stone-400 text-xs">
                {slotDialogData.date} · {slotDialogData.startTime}–{slotDialogData.endTime}
                {!slotDialogData.isWorking && <span className="text-amber-500 ml-1">(нерабочее)</span>}
              </p>
              <Button onClick={() => {
                setSlotDialogOpen(false);
                setNewAppt(a => ({ ...a, date: slotDialogData.date, time: slotDialogData.startTime, patient_name: '', patient_phone: '', patient_email: '', format_id: '', notes: '', room_id: '' }));
                setSelectedPatient(null); setPatientSearch('');
                setGroupMode(false); setGroupPatients([]);
                setCreateOpen(true);
              }} className="w-full bg-amber-700 hover:bg-amber-600 text-white">
                <CalendarDays size={14} className="mr-2" /> Создать запись
              </Button>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handleAddOverride('add')}
                  className="flex-1 bg-green-800/80 hover:bg-green-700 text-white text-xs">
                  + Рабочие часы
                </Button>
                <Button size="sm" onClick={() => handleAddOverride('remove')}
                  className="flex-1 bg-red-800/80 hover:bg-red-700 text-white text-xs">
                  − Убрать часы
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ══ Chessboard View (Rooms × Time) ══ */
function ChessboardView({ onAppointmentClick }) {
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragApptId, setDragApptId] = useState(null);
  const [dragOverRoom, setDragOverRoom] = useState(null);

  const HOURS = Array.from({ length: 14 }, (_, i) => i + 8);
  const SLOT = 30;

  const fetchData = useCallback(() => {
    setLoading(true);
    api.get(`/rooms/chessboard?date=${date}`)
      .then(({ data: d }) => setData(Array.isArray(d) ? d : []))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const prevDay = () => { const d = new Date(date); d.setDate(d.getDate() - 1); setDate(format(d, 'yyyy-MM-dd')); };
  const nextDay = () => { const d = new Date(date); d.setDate(d.getDate() + 1); setDate(format(d, 'yyyy-MM-dd')); };

  const timeToY = (t) => { const [h, m] = t.split(':').map(Number); return (h - 8) * 2 + Math.floor(m / 30); };
  const slotHeight = 36;

  // DnD handlers
  const handleDragStart = (e, apptId) => {
    setDragApptId(apptId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', apptId);
  };
  const handleDragEnd = () => { setDragApptId(null); setDragOverRoom(null); };
  const handleColumnDragOver = (e, roomId) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverRoom(roomId); };
  const handleColumnDragLeave = () => setDragOverRoom(null);
  const handleColumnDrop = async (e, roomId) => {
    e.preventDefault();
    const apptId = e.dataTransfer.getData('text/plain');
    setDragApptId(null);
    setDragOverRoom(null);
    if (!apptId || !roomId) return;
    // Find current room of the appointment
    let currentRoomId = '';
    for (const room of data) {
      if (room.appointments?.some(a => a.id === apptId)) { currentRoomId = room.id; break; }
    }
    if (currentRoomId === roomId) return; // same room, no-op
    try {
      await api.put(`/rooms/assign-room?appt_id=${apptId}&room_id=${roomId}`);
      toast.success('Кабинет изменён');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка перемещения');
    }
  };

  if (loading) return <p className="text-stone-500 text-center py-8">Загрузка шахматки...</p>;
  if (data.length === 0) return (
    <Card className="bg-stone-900 border-stone-800">
      <CardContent className="p-8 text-center">
        <DoorOpen size={36} className="text-stone-700 mx-auto mb-3" />
        <p className="text-stone-500 text-sm">Нет кабинетов. Добавьте кабинеты в настройках.</p>
      </CardContent>
    </Card>
  );

  return (
    <Card className="bg-stone-900 border-stone-800 animate-fade-in">
      <CardContent className="p-0">
        {/* Date navigation */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-800">
          <Button variant="ghost" size="sm" onClick={prevDay} className="text-stone-400 hover:text-stone-200"><ChevronLeft size={16} /></Button>
          <div className="flex items-center gap-3">
            <Input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="bg-stone-950/50 border-stone-800 text-stone-200 text-sm w-40 h-8" />
            <span className="text-stone-300 text-sm font-medium capitalize">
              {(() => { try { return format(parseISO(date), 'EEEE, d MMMM', { locale: ru }); } catch { return ''; } })()}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={nextDay} className="text-stone-400 hover:text-stone-200"><ChevronRight size={16} /></Button>
        </div>
        {dragApptId && <p className="text-[10px] text-amber-500/60 text-center py-1">Перетащите запись в другой кабинет</p>}

        {/* Grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[600px]" style={{ display: 'grid', gridTemplateColumns: `60px repeat(${data.length}, 1fr)` }}>
            {/* Header */}
            <div className="sticky top-0 z-10 bg-stone-900 border-b border-r border-stone-800 p-2" />
            {data.map(room => (
              <div key={room.id}
                onDragOver={e => handleColumnDragOver(e, room.id)}
                onDragLeave={handleColumnDragLeave}
                onDrop={e => handleColumnDrop(e, room.id)}
                className={`sticky top-0 z-10 border-b border-stone-800 p-2 text-center transition-colors ${
                  dragOverRoom === room.id ? 'bg-amber-900/20 ring-1 ring-inset ring-amber-700/40' : 'bg-stone-900'
                }`}>
                <p className="text-xs font-medium text-teal-400 truncate">{room.name}</p>
                <p className="text-[10px] text-stone-600">{room.description || ''}</p>
              </div>
            ))}

            {/* Time rows */}
            {HOURS.map(h => [0, 30].map(m => {
              const timeStr = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
              const slotIdx = (h - 8) * 2 + (m === 30 ? 1 : 0);
              return [
                <div key={`t-${timeStr}`} className="border-r border-stone-800/50 px-1 flex items-center justify-end"
                  style={{ height: slotHeight, borderTop: m === 0 ? '1px solid rgb(41 37 36 / 0.5)' : 'none' }}>
                  {m === 0 && <span className="text-[10px] text-stone-600">{timeStr}</span>}
                </div>,
                ...data.map(room => {
                  const appt = room.appointments?.find(a => {
                    const aStart = timeToY(a.time);
                    const aSlots = Math.ceil((a.duration || 60) / SLOT);
                    return slotIdx >= aStart && slotIdx < aStart + aSlots;
                  });
                  const isStart = appt && timeToY(appt.time) === slotIdx;
                  const aSlots = appt ? Math.ceil((appt.duration || 60) / SLOT) : 0;

                  if (appt && !isStart) return <div key={`${room.id}-${timeStr}`} />;

                  return (
                    <div key={`${room.id}-${timeStr}`}
                      onDragOver={e => handleColumnDragOver(e, room.id)}
                      onDrop={e => handleColumnDrop(e, room.id)}
                      className={`border-l border-stone-800/30 relative ${!appt ? 'border-t border-stone-800/20' : ''} ${
                        dragOverRoom === room.id && !appt ? 'bg-amber-900/10' : ''
                      }`}
                      style={{ height: appt && isStart ? slotHeight * aSlots : slotHeight }}>
                      {appt && isStart && (
                        <div
                          draggable
                          onDragStart={e => handleDragStart(e, appt.id)}
                          onDragEnd={handleDragEnd}
                          onClick={() => onAppointmentClick(appt)}
                          className={`absolute inset-0.5 rounded-md px-2 py-1 text-left overflow-hidden transition-all cursor-grab active:cursor-grabbing hover:brightness-110 ${
                            dragApptId === appt.id ? 'opacity-40 scale-95' : ''
                          }`}
                          style={{
                            background: 'rgba(217, 119, 6, 0.2)',
                            border: '1px solid rgba(217, 119, 6, 0.3)',
                            height: slotHeight * aSlots - 4,
                          }}>
                          <p className="text-[10px] font-medium text-amber-400 truncate">{appt.patient_name}</p>
                          <p className="text-[9px] text-stone-500 truncate">{appt.doctor_name} · {appt.time}</p>
                          <p className="text-[9px] text-stone-600 truncate">{appt.format_name}</p>
                        </div>
                      )}
                    </div>
                  );
                })
              ];
            }).flat())}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/* ══ ICD-10 Searchable Field (MEDICAL only) ══ */
function ICD10Field({ apptId, currentCode }) {
  const { isMedical } = useIndustry();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(currentCode);
  const [open, setOpen] = useState(false);

  useEffect(() => { setSelected(currentCode); }, [currentCode]);

  if (!isMedical) return null;

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 1) { setResults([]); return; }
    try {
      const { data } = await api.get(`/icd10/search?q=${encodeURIComponent(q)}`);
      setResults(data);
      setOpen(true);
    } catch { setResults([]); }
  };

  const selectCode = async (code) => {
    setSelected(`${code.code} — ${code.name}`);
    setSearch('');
    setOpen(false);
    try {
      await api.put(`/appointments/${apptId}/session-notes`, { icd10_code: `${code.code} — ${code.name}` });
    } catch { /* silent */ }
  };

  return (
    <div className="pt-2 border-t border-stone-800/50">
      <label className="text-stone-400 text-xs font-medium mb-1.5 block">Диагноз (МКБ-10)</label>
      {selected && (
        <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded bg-sky-900/15 border border-sky-900/30">
          <span className="text-sky-400 text-xs flex-1">{selected}</span>
          <button onClick={async () => {
            setSelected('');
            try { await api.put(`/appointments/${apptId}/session-notes`, { icd10_code: '' }); } catch {}
          }} className="text-stone-600 hover:text-red-400"><X size={12} /></button>
        </div>
      )}
      <div className="relative">
        <Input value={search} onChange={e => doSearch(e.target.value)}
          placeholder="Поиск: F32, депрессия, СДВГ..."
          onFocus={() => { if (results.length) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          className="bg-stone-950/50 border-stone-800 text-stone-200 text-xs h-8" />
        {open && results.length > 0 && (
          <div className="absolute z-30 top-full left-0 right-0 mt-1 max-h-40 overflow-y-auto bg-stone-900 border border-stone-700 rounded-lg shadow-xl">
            {results.map(c => (
              <button key={c.code} onMouseDown={e => e.preventDefault()} onClick={() => selectCode(c)}
                className="w-full text-left px-3 py-1.5 hover:bg-stone-800 transition-colors border-b border-stone-800/50 last:border-0">
                <span className="text-sky-400 text-xs font-mono mr-2">{c.code}</span>
                <span className="text-stone-300 text-xs">{c.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
